// Tiny DOM helper — same idiom as Fabrika's web UI, no framework.

type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

export function el(
  tag: string,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "class") {
      node.className = String(v);
    } else if (k === "value") {
      (node as HTMLInputElement).value = String(v);
    } else {
      node.setAttribute(k, String(v));
    }
  }
  for (const c of children) {
    node.append(c instanceof Node ? c : document.createTextNode(c));
  }
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

// True while the user is mid-interaction with a subtree we're about to
// re-render: focus is inside it, or an input/textarea holds typed text.
// Background refetches rebuild these subtrees from scratch, which would yank
// focus, discard half-typed text, and snap an open <details> (whose <summary>
// holds the focus) shut — so callers defer the rebuild until this goes false.
// We deliberately don't key on `details[open]` alone: some details render open
// by default (e.g. the agent log), which would block refetch indefinitely.
export function isInteracting(node: HTMLElement): boolean {
  const active = document.activeElement;
  if (active && active !== document.body && node.contains(active)) return true;
  for (const f of node.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea")) {
    if (f.value.trim()) return true;
  }
  return false;
}
